import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft,
  BadgeAlert,
  Calendar,
  Car,
  Download,
  FileText,
  Link2Off,
  MapPin,
  Pencil,
  Phone,
  Plus,
  Trash2,
  Users,
} from 'lucide-react';
import { differenceInDays, parseISO } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatDate, formatDateTime } from '@/lib/format';
import { extractErrorMessage } from '@/lib/api-client';
// Driver is fetched directly via api.get below
import { DriverFormDialog } from './driver-form-dialog';
import type { Driver } from './types';
import {
  useDriverDocuments,
  useCreateDriverDocument,
  useDeleteDriverDocument,
  getUploadDownloadUrl,
} from '@/features/documents/api';
import { DRIVER_DOCUMENT_LABELS, formatFileSize } from '@/features/documents/types';
import { UploadDropzone } from '@/features/uploads/upload-dropzone';
import { useAssignmentsByDriver, useEndAssignment } from '@/features/assignments/api';
import { AssignmentFormDialog } from '@/features/assignments/assignment-form-dialog';

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-right text-sm font-medium">{value ?? '—'}</span>
    </div>
  );
}



export function DriverDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Fetch driver directly via the drivers API
  const [driver, setDriver] = useState<Driver | null>(null);
  const [loading, setLoading] = useState(true);

  // Direct fetch on mount
  useState(() => {
    if (!id) return;
    import('@/lib/api-client').then(({ api: apiClient }) => {
      apiClient
        .get<{ data: Driver }>(`/drivers/${id}`)
        .then((res) => {
          setDriver(res.data.data);
          setLoading(false);
        })
        .catch(() => setLoading(false));
    });
  });

  const docsQuery = useDriverDocuments(id ?? '', !!id && !loading);
  const assignmentsQuery = useAssignmentsByDriver(id ?? '', !!id && !loading);
  const createDoc = useCreateDriverDocument(id ?? '');
  const deleteDoc = useDeleteDriverDocument(id ?? '');

  const [editOpen, setEditOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const endAssignment = useEndAssignment();

  if (loading) {
    return (
      <div className="page space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!driver) {
    return (
      <div className="page">
        <p className="text-sm text-muted-foreground">Motorista não encontrado.</p>
        <Button variant="outline" onClick={() => navigate('/app/drivers')}>
          <ArrowLeft className="size-4" /> Voltar
        </Button>
      </div>
    );
  }

  const d = driver;
  const docs = docsQuery.data?.data ?? [];
  const assignments = assignmentsQuery.data?.data ?? [];

  const cnhDaysLeft = differenceInDays(parseISO(d.cnhExpiresAt), new Date());
  const cnhBadge =
    cnhDaysLeft < 0
      ? { label: 'Vencida', variant: 'destructive' as const }
      : cnhDaysLeft <= 30
        ? { label: `Vence em ${cnhDaysLeft}d`, variant: 'warning' as const }
        : { label: 'Válida', variant: 'success' as const };

  return (
    <div className="page">
      <header className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="flex items-start gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/app/drivers')}>
            <ArrowLeft className="size-5" />
          </Button>
          <div className="space-y-1">
            <h1 className="text-2xl font-semibold tracking-tight md:text-3xl">{d.name}</h1>
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <span className="font-mono">{d.cpf}</span>
              <Badge variant={cnhBadge.variant}>CNH {cnhBadge.label}</Badge>
            </p>
          </div>
        </div>
        <Button onClick={() => setEditOpen(true)}>
          <Pencil className="size-4" /> Editar
        </Button>
      </header>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">
            <Users className="size-4" /> Visão geral
          </TabsTrigger>
          <TabsTrigger value="documents">
            <FileText className="size-4" /> Documentos ({docs.length})
          </TabsTrigger>
          <TabsTrigger value="history">
            <Car className="size-4" /> Veículos ({assignments.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Users className="size-4" /> Dados pessoais
                </CardTitle>
              </CardHeader>
              <CardContent className="divide-y divide-border">
                <InfoRow label="Nome" value={d.name} />
                <InfoRow label="CPF" value={<span className="font-mono">{d.cpf}</span>} />
                <InfoRow label="RG" value={d.rg} />
                <InfoRow label="Nascimento" value={formatDate(d.birthDate)} />
                <Separator />
                <InfoRow
                  label="Telefone"
                  value={
                    d.phone ? (
                      <span className="inline-flex items-center gap-1.5">
                        <Phone className="size-3.5" /> {d.phone}
                      </span>
                    ) : null
                  }
                />
                <InfoRow label="E-mail" value={d.email} />
                <InfoRow
                  label="Endereço"
                  value={
                    d.city ? (
                      <span className="inline-flex items-center gap-1.5">
                        <MapPin className="size-3.5" />
                        {d.address ? `${d.address}, ` : ''}
                        {d.city}/{d.state}
                      </span>
                    ) : null
                  }
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <BadgeAlert className="size-4" /> CNH
                </CardTitle>
              </CardHeader>
              <CardContent className="divide-y divide-border">
                <InfoRow label="Número" value={<span className="font-mono">{d.cnh}</span>} />
                <InfoRow label="Categoria" value={<Badge variant="secondary">{d.cnhCategory}</Badge>} />
                <InfoRow label="Primeira habilitação" value={formatDate(d.cnhFirstIssueAt)} />
                <InfoRow
                  label="Validade"
                  value={
                    <span className="inline-flex items-center gap-2">
                      {formatDate(d.cnhExpiresAt)}
                      <Badge variant={cnhBadge.variant}>{cnhBadge.label}</Badge>
                    </span>
                  }
                />
                <InfoRow
                  label="Pontos"
                  value={
                    <span className={d.cnhPoints >= 20 ? 'font-semibold text-destructive' : ''}>
                      {d.cnhPoints}/40
                    </span>
                  }
                />
                <Separator />
                <InfoRow label="Cadastrado em" value={formatDateTime(d.createdAt)} />
                <InfoRow label="Última atualização" value={formatDateTime(d.updatedAt)} />
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="documents">
          <div className="space-y-4">
            <UploadDropzone
              purpose="driver_document"
              label="Envie CNH, comprovante de endereço ou outros documentos"
              onUploaded={async (uploaded) => {
                try {
                  await createDoc.mutateAsync({
                    type: 'other',
                    description: uploaded.filename,
                    uploadId: uploaded.uploadId,
                  });
                  toast.success('Documento adicionado');
                } catch (err) {
                  toast.error(extractErrorMessage(err));
                }
              }}
            />

            {docs.length === 0 && !docsQuery.isLoading && (
              <p className="py-8 text-center text-sm text-muted-foreground">
                Nenhum documento cadastrado.
              </p>
            )}

            {docs.map((doc) => (
              <Card key={doc.id}>
                <CardContent className="flex items-center justify-between gap-4 p-4">
                  <div className="flex items-center gap-3">
                    <div className="flex size-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <FileText className="size-5" />
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">
                        {DRIVER_DOCUMENT_LABELS[doc.type as keyof typeof DRIVER_DOCUMENT_LABELS] ?? doc.type}
                        {doc.description && ` — ${doc.description}`}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {doc.upload.filename} · {formatFileSize(doc.upload.sizeBytes)}
                        {doc.expiresAt && (
                          <>
                            {' · '}
                            <Calendar className="inline size-3" /> Vence {formatDate(doc.expiresAt)}
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-8"
                      onClick={async () => {
                        const url = await getUploadDownloadUrl(doc.upload.id);
                        window.open(url, '_blank');
                      }}
                    >
                      <Download className="size-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-8 text-destructive"
                      onClick={async () => {
                        await deleteDoc.mutateAsync(doc.id);
                        toast.success('Documento removido');
                      }}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="history">
          <div className="space-y-4">
            <div className="flex justify-end">
              <Button onClick={() => setAssignOpen(true)}>
                <Plus className="size-4" />
                Vincular veículo
              </Button>
            </div>

            {assignments.length === 0 && !assignmentsQuery.isLoading && (
              <Card>
                <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
                  <Car className="size-8 text-muted-foreground" />
                  <p className="text-sm font-medium">Nenhum veículo vinculado</p>
                  <p className="text-xs text-muted-foreground">
                    Clique em "Vincular veículo" para associar um veículo a este motorista.
                  </p>
                </CardContent>
              </Card>
            )}

            {assignments.map((a) => (
              <Card key={a.id}>
                <CardContent className="flex items-center justify-between gap-4 p-4">
                  <div className="flex items-center gap-3">
                    <div className="flex size-10 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                      <Car className="size-5" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">
                        <span className="font-mono">{a.vehicle.plate}</span>
                        {' — '}
                        {a.vehicle.brand} {a.vehicle.model}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Desde {formatDate(a.startedAt)}
                        {a.notes && ` · ${a.notes}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {a.endedAt ? (
                      <Badge variant="outline">Encerrado {formatDate(a.endedAt)}</Badge>
                    ) : (
                      <Badge variant="success">Ativo</Badge>
                    )}
                    {!a.endedAt && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-8 text-destructive"
                        title="Encerrar vínculo"
                        onClick={async () => {
                          try {
                            await endAssignment.mutateAsync({ id: a.id });
                            toast.success('Vínculo encerrado');
                          } catch (err) {
                            toast.error(extractErrorMessage(err));
                          }
                        }}
                      >
                        <Link2Off className="size-4" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <DriverFormDialog open={editOpen} onOpenChange={setEditOpen} driver={d} />
      {d && (
        <AssignmentFormDialog
          open={assignOpen}
          onOpenChange={setAssignOpen}
          mode={{ kind: 'forDriver', driverId: d.id, driverLabel: d.name }}
        />
      )}
    </div>
  );
}
